/**
 * API Response Analyzer
 * 
 * Utility functions for extracting and analyzing API response data,
 * specifically designed to locate and extract Results objects from
 * various API response structures.
 */

type ResultsLocation = {
  path: string;
  data: any;
  type: string;
};

/**
 * Deep search for Results objects within an API response
 * 
 * @param obj - The API response object to analyze
 * @returns An array of found Results objects with their paths and types
 */
export const findResultsObjects = (obj: any): ResultsLocation[] => {
  const results: ResultsLocation[] = [];
  
  // Recursively search through object
  const searchObject = (object: any, path: string = 'root'): void => {
    if (!object || typeof object !== 'object') return;
    
    // Check if this is an array
    if (Array.isArray(object)) {
      // Check first item for sample analysis (if available)
      if (object.length > 0) {
        searchObject(object[0], `${path}[0]`);
      }
      return;
    }
    
    // Look for Results property (case-insensitive)
    const keys = Object.keys(object);
    
    for (const key of keys) {
      const lowerKey = key.toLowerCase();
      
      // Direct match for 'results'
      if (lowerKey === 'results') {
        results.push({
          path: `${path}.${key}`,
          data: object[key],
          type: Array.isArray(object[key]) ? 'array' : typeof object[key]
        });
      }
      // Partial match for 'result' in key name
      else if (lowerKey.includes('result')) {
        results.push({
          path: `${path}.${key}`,
          data: object[key],
          type: Array.isArray(object[key]) ? 'array' : typeof object[key]
        });
      }
      
      // Continue recursive search
      if (typeof object[key] === 'object' && object[key] !== null) {
        searchObject(object[key], `${path}.${key}`);
      }
    }
  };
  
  searchObject(obj);
  return results;
};

/**
 * Extract error information from an API response
 * 
 * @param response - The API response object to analyze
 * @returns Structured error information or null if no error
 */
export const extractErrorInfo = (response: any): { code: string; message: string; details: any } | null => {
  if (!response) return null;
  
  // Check for standard error property
  if (response.error) {
    return {
      code: response.error.code || 'unknown',
      message: response.error.message || 'Unknown error',
      details: response.error
    };
  }
  
  // Check for nested error structures
  const errorLocations = ['error', 'errors', 'fault', 'exception'];
  
  for (const key in response) {
    if (errorLocations.includes(key.toLowerCase()) && response[key]) {
      return {
        code: response[key].code || 'unknown',
        message: response[key].message || 'Unknown error',
        details: response[key]
      };
    }
  }
  
  return null;
};

/**
 * Log an API response to console with special formatting for analysis
 * 
 * @param title - Description of what is being logged
 * @param response - The API response object
 */
export const logApiResponse = (title: string, response: any): void => {
  console.group(`%c[API Analysis] ${title}`, 'color: #3498db; font-weight: bold;');
  
  // Log general response info
  console.log('Response Status:', response.status || 'N/A');
  
  // Look for results objects
  const resultsFound = findResultsObjects(response);
  
  if (resultsFound.length > 0) {
    console.log('%cResults objects found:', 'color: #2ecc71; font-weight: bold;');
    resultsFound.forEach(result => {
      console.log(`Path: ${result.path} (${result.type})`);
      console.log(result.data);
    });
  } else {
    console.log('%cNo Results objects found', 'color: #e74c3c;');
  }
  
  // Check for errors
  const errorInfo = extractErrorInfo(response);
  if (errorInfo) {
    console.log('%cError detected:', 'color: #e74c3c; font-weight: bold;');
    console.log(`Code: ${errorInfo.code}`);
    console.log(`Message: ${errorInfo.message}`);
    console.log('Details:', errorInfo.details);
  }
  
  // Log full response for reference
  console.log('Full response:', response);
  
  console.groupEnd();
};

/**
 * Convert API response to a downloadable JSON file
 * 
 * @param response - The API response to download
 * @returns URL to download the JSON file
 */
export const createDownloadableResponse = (response: any): string => {
  const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
  return URL.createObjectURL(blob);
};

export default {
  findResultsObjects,
  extractErrorInfo,
  logApiResponse,
  createDownloadableResponse
};
